import React, { useEffect, useRef, useState } from 'react';
import Loader from './Loader';
import { ButtonSize } from '../../../components/Button';
import { useWallet } from '../../../hooks/useWallet';
import { Button } from '../../../components/Button';
import { ButtonVariant } from '../../../config/colors';
import { formatAmountWithSATS, formatNumberWithCommas } from '../../../utils/Utils';
import { InputSearch } from '../../../components/InputSearch';
import { Transaction } from '../../../models/Transactions';
import Portal from '../../../components/Portal';
import { useTransaction } from '../../../hooks/useTransaction';
import { getAmountNumber } from '../wallet/SendTxAmount';
import { WalletActiveRoute } from '../../../components/WalletActiveRoute';
import { SendTransactionStateRecipient } from '../../../store/slices/transactionSlice';
import { WalletStatus } from '../../../store/slices/walletSlice';

interface DialogSendWalletProps {
  visible: boolean;
  onClose: () => void;
  position: { top: number, right: number };
  transactionStateRecipient: SendTransactionStateRecipient | null;
}

const DialogSendWallet: React.FC<DialogSendWalletProps> = ({ visible, onClose, position, transactionStateRecipient }) => {
  const {loading, error, payTransaction, scanRecipient} = useTransaction();
  const {balance} = useWallet();
  const {scanData, recipient, amount, comment, setTransactionAmount, maxAmountSpendable, setTransactionComment} = useTransaction();
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const {status} = useWallet();

  useEffect(() => {
    if(!visible) {
      handleAmountChange('');
      setTransaction(null);
    } else {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [visible])

  useEffect(() => {
    if(transactionStateRecipient && status === WalletStatus.ACTIVE) {
      scanRecipient(transactionStateRecipient);
    }
  }, [transactionStateRecipient, status]);

  const handleChangeComment = (comment: string) => {
    const maxLength = scanData?.commentAllowed ?? 250;
    const croppedComment = comment.slice(0, maxLength);
    setTransactionComment(croppedComment);
  }

  const handleAmountChange = (newAmount: string) => {
    const newAmountNumber = getAmountNumber(newAmount);
    if(newAmountNumber <= 9999999)
      setTransactionAmount(newAmountNumber);
  };

  const handleButtonClick = async () => {
    const transaction = await payTransaction();

    if(transaction) {
      setTransaction(transaction);
    }
  }

  if(visible)
    return (
      <Portal isOpen={visible} onClose={onClose} className="w-[300px] fixed m-4" style={{ top: position.top, right: position.right }}>
        <div className='flex flex-col h-full bg-brand-bg-2 rounded-3xl p-4 min-h-[250px] justify-center align-center'>
          <WalletActiveRoute outsidePadding={false} loaderIfNotSynced={true}>
          {
            loading ? <Loader small /> :
            transaction ? <div className="flex flex-col justify-center items-center flex-1 gap-6 p-4">
              <div className="flex flex-col items-center">
                  <img src={"/images/payment_success_transparent.png"} alt="" className="w-[120px]" />
              </div>

              {
                Math.abs(transaction?.amount ?? 0) > 0 ? <div className="flex flex-col items-center">
                  <div className={`font-semibold text-3xl`}>
                      {formatAmountWithSATS(Math.abs(transaction?.amount ?? 0))}
                  </div>
                </div> : <div>
                  <div className={`font-semibold text-xl text-center`}>Zap in process...</div>
                  <div className={`text-center`}>You may close this tab</div>
                </div>
              }

              <div className='flex flex-row justify-center'>
                <Button
                  variant={ButtonVariant.primary}
                  size={ButtonSize.medium}
                  onClick={onClose}
                >
                    CONFIRMED!
                  </Button>
                </div>
              
            </div> : 
              <div className='flex-grow text-brand-text-2 flex flex-col justify-center align-center gap-4'>
                <InputSearch
                  ref={inputRef}
                  placeholder="Amount"
                  value={formatNumberWithCommas(amount && amount > 0 ? amount?.toString() : '')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAmountChange(e.target.value)}
                  onClear={() => handleAmountChange('')}
                  inputMode="numeric"
                />

                {(balance && balance > 0) ? (
                  <div className="flex flex-row justify-center">
                      <Button
                          size={ButtonSize.small}
                          onClick={() => handleAmountChange(formatNumberWithCommas(Math.floor(maxAmountSpendable).toString() ?? ''))}
                          variant={ButtonVariant.unselected}
                          type="button"
                      >
                          Max {formatAmountWithSATS(Math.floor(maxAmountSpendable) ?? 0)}
                      </Button>
                  </div>
                ) : null}

                {
                  ((scanData?.commentAllowed && scanData?.commentAllowed > 0) ||
                  recipient?.bulkPayment) && (
                    <div className='mt-4'>
                      <InputSearch
                          placeholder="Message"
                          value={comment ?? ''}
                          onClear={() => handleChangeComment('')}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeComment(e.target.value)}
                      />
                  </div>
                  )
                }

                {error && error !== '' && <div className="text-brand-red text-center">{error}</div>}

                <div className='flex flex-row justify-center mt-4'>
                  <Button
                    variant={ButtonVariant.primary}
                    size={ButtonSize.medium}
                    onClick={handleButtonClick}
                    disabled={loading || amount === 0 || amount === undefined}
                    loading={loading}
                  >
                    SEND IT
                  </Button>
                </div>
              </div>
          }
          </WalletActiveRoute>
        </div>
      </Portal>
    );
  
  return null;
};

export default DialogSendWallet;